import { useContext } from "react"
import { Meet } from "../meet/index.jsx"
import { UserContext } from "../../context/UserContext.js"
import { MainLoggedInLayout } from "../../components/layouts/main.loggedIn.layout.jsx"
import { Box, Button, Chip, Typography } from "@mui/material"
import { Android, Apple, CalendarMonth, EmojiFoodBeverage, WavingHand } from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { Login } from "../../components/account/login.jsx"
import { DownloadAppPopup } from "../../components/DownloadAppPopup.jsx"

const PRIDE = ['#E40303', '#FF8C00', '#FFED00', '#008026', '#24408E', '#732982']

const IOS_APP_URL = 'https://apps.apple.com/us/app/make-gay-friends/id6478235888'
const ANDROID_APP_URL = 'https://play.google.com/store/apps/details?id=com.wizcomputer.makegayfriends&hl=en_US'

const FeatureCard = ({ icon, title, description, color }) => (
    <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 2.5,
        borderRadius: 3,
        background: '#fff',
        border: '1px solid #ececf0',
        flex: 1,
        minWidth: { xs: '100%', sm: 200 },
    }}>
        <Box sx={{
            width: 42, height: 42, borderRadius: 2,
            background: `${color}18`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            '& .MuiSvgIcon-root': { color, fontSize: 22 },
        }}>
            {icon}
        </Box>
        <Typography fontWeight={800} fontSize={15} color="#23232b">{title}</Typography>
        <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{description}</Typography>
    </Box>
)

const LandingPage = () => {
    const navigate = useNavigate()

    return (
        <Box sx={{ minHeight: '100vh', background: '#f6f7fb', display: 'flex', flexDirection: 'column' }}>
            <DownloadAppPopup />

            {/* Pride strip */}
            <Box sx={{ height: 5, background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)', flexShrink: 0 }} />

            {/* Nav bar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: { xs: 2, sm: 4 }, py: 2, background: '#fff', borderBottom: '1px solid #ececf0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src="https://storage.googleapis.com/events.makegayfriends.com/assets/makgf_logo.png" alt="Logo" style={{ height: 36, width: 'auto' }} />
                    <Typography fontWeight={900} fontSize={18} sx={{
                        background: 'linear-gradient(90deg,#E40303,#732982)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        display: { xs: 'none', sm: 'block' },
                    }}>
                        MakeGayFriends
                    </Typography>
                </Box>
            </Box>

            {/* Hero */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                px: { xs: 2, sm: 4 },
                py: { xs: 6, sm: 10 },
            }}>
                {/* Pride dots */}
                <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                    {PRIDE.map(c => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: '50%', background: c }} />
                    ))}
                </Box>

                <Typography variant="h3" fontWeight={900} sx={{
                    maxWidth: 640,
                    mb: 2,
                    fontSize: { xs: '2rem', sm: '2.75rem' },
                    lineHeight: 1.15,
                    color: '#23232b',
                }}>
                    A home for the{' '}
                    <Box component="span" sx={{
                        background: 'linear-gradient(90deg,#E40303,#FF8C00,#FFED00,#008026,#24408E,#732982)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                    }}>
                        LGBTQ+ community
                    </Box>
                </Typography>

                <Typography variant="h6" color="text.secondary" fontWeight={400} sx={{ maxWidth: 500, mb: 4, fontSize: { xs: 16, sm: 18 }, lineHeight: 1.6 }}>
                    Meet queer people, discover events, and find your people — all in a safe, inclusive space.
                </Typography>

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center', mb: 5 }}>
                    <Chip label="Pride-friendly" sx={{ background: '#E403031a', color: '#E40303', fontWeight: 700 }} />
                    <Chip label="Inclusive" sx={{ background: '#0080261a', color: '#008026', fontWeight: 700 }} />
                    <Chip label="Global community" sx={{ background: '#7329821a', color: '#732982', fontWeight: 700 }} />
                </Box>

                {/* Feature cards */}
                <Box sx={{
                    display: 'flex',
                    gap: 2,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    maxWidth: 860,
                    width: '100%',
                    mb: 6,
                }}>
                    <FeatureCard
                        icon={<WavingHand />}
                        color="#008026"
                        title="Meet People"
                        description="Swipe through profiles and say hey to queer people near you and around the world."
                    />
                    <FeatureCard
                        icon={<CalendarMonth />}
                        color="#E40303"
                        title="Events"
                        description="Discover LGBTQ+ events near you, RSVP, and start planning in The Pre-Mixer before you even arrive."
                    />
                    <FeatureCard
                        icon={<EmojiFoodBeverage />}
                        color="#24408E"
                        title="The Tea"
                        description="See what's brewing — public posts from across the network, or just your friends."
                    />
                    {/* Explore and Quick Meets hidden for now — not yet fixed/tested/perfected */}
                </Box>

                {/* App download CTA */}
                <Box sx={{
                    width: '100%',
                    maxWidth: 560,
                    borderRadius: 4,
                    p: { xs: 2.5, sm: 3 },
                    mb: 5,
                    background: 'linear-gradient(120deg,#f3f0ff,#fff0f6)',
                    border: '1px solid rgba(115,41,130,0.15)',
                }}>
                    <Typography fontWeight={800} fontSize={16} color="#23232b" sx={{ mb: 0.5 }}>
                        Get the app
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Take MakeGayFriends with you — download the app for iOS or Android.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                            href={IOS_APP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            startIcon={<Apple />}
                            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#fff' }}
                        >
                            Download on iOS
                        </Button>
                        <Button
                            href={ANDROID_APP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="outlined"
                            startIcon={<Android sx={{ color: '#3DDC84' }} />}
                            sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 700, background: '#fff' }}
                        >
                            Get it on Android
                        </Button>
                    </Box>
                </Box>

                {/* Hero image */}
                <Box sx={{
                    width: '100%',
                    maxWidth: 560,
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 12px 48px rgba(0,0,0,0.14)',
                    mb: 2,
                }}>
                    <img
                        src="https://storage.googleapis.com/events.makegayfriends.com/event_images/fIsForFriends.png"
                        alt="LGBTQ+ Friends"
                        style={{ width: '100%', display: 'block', objectFit: 'fill', maxHeight: 300 }}
                    />
                </Box>

                <Typography variant="caption" color="text.disabled">
                    Powered by Mastodon · Open & decentralized
                </Typography>
            </Box>

            {/* Footer */}
            <Box sx={{ textAlign: 'center', py: 2, px: 2, borderTop: '1px solid #ececf0', background: '#fff' }}>
                <Typography variant="caption" color="text.disabled">
                    © {new Date().getFullYear()} MakeGayFriends.com — LGBTQ+ Community Platform
                </Typography>
            </Box>

            {/* Login bar pinned to bottom */}
            <Box sx={{ position: 'fixed', bottom: 0, width: '100%', zIndex: 100 }}>
                <Login />
            </Box>
        </Box>
    )
}

export const Homepage = () => {
    const { userDetails } = useContext(UserContext)

    if (userDetails?.id) return <Meet />

    return <LandingPage />
}
